import React, { useState, useRef, ReactNode } from 'react'
import { useLoadingOverlay } from './useLoadingOverlay'
import { useAlert } from './useAlert'

import { fetchPost } from '@oshcut/oshlib'
import Log from '../logs'

export function useBlockingApi() {

    const [overlayContent, showLoadingOverlay, hideLoadingOverlay] = useLoadingOverlay()
    const [alertContent, showAlert] = useAlert()

    const timer = useRef<any>(null)
    const abortController = useRef<AbortController | null>(null)

    async function execute<T = unknown>(url: string, obj: any, options: {
        reason?: string,
        timeout?: number,
        timeoutMessage?: string,
        timeoutCallback?: () => void,
        errorMessage?: string | ((serverMessage: string) => ReactNode),
        hideOverlay?: boolean,
        resultCallback?: (result: Awaited<T> | { error: any }) => void
    }) {

        if (timer.current !== null) {
            Log.error('Blocking API call interrupted another API call already in progress')
            Log.error('In progress: ')
            Log.error(url)
            Log.error(obj)
            Log.error('New')
            Log.error(url)
            Log.error(obj)
            cancel()
        }

        abortController.current = new AbortController()

        let reason = options.reason ? options.reason : ''
        let timeout = options.timeout ? options.timeout : 10000
        let timeoutMessage = options.timeoutMessage ? options.timeoutMessage : null
        let timeoutCallback = options.timeoutCallback
        let errorMessage = options.errorMessage ? options.errorMessage : null
        let hideOverlay = options.hideOverlay ? options.hideOverlay : null

        timer.current = setTimeout(() => handleTimeout(timeoutMessage, timeoutCallback), timeout)
        if (!hideOverlay) {
            showLoadingOverlay(reason)
        }
        let result
        try {
            result = await fetchPost<T>(url, obj, null, {}, abortController.current)
            hideLoadingOverlay()
        }
        catch (e: any) {
            Log.error(e)
            // e.serverMessage is set if the server returned an error field in the response JSON.
            // This can be the result of a hard error caught by the app-wide handler, or a more mundane error
            // like failed auth
            if (e.serverMessage) {
                if (typeof errorMessage === 'function') {
                    showAlert('Error', errorMessage(e.serverMessage))
                } else if (typeof errorMessage === 'string') {
                    showAlert('Error', errorMessage + e.serverMessage)
                }
                result = { error: e.serverMessage }
            }
            // If e.serverMessage is not set, the error was a client-side exception.  Failed TCP COM, or
            // bad server response (like a 404) could cause this issue.
            // We'll display an error popup in this case
            else {
                // On abort, deal with the error a little differently, since the timeout would have been initiated by us
                if (e.message === 'The user aborted a request.') {
                    result = { error: 'Timed-out.  Please try again.' }
                }
                else {
                    showAlert('Error', 'We apologize for the inconvenience, but our system had an unexpected error.  Please refresh and try again, or contact OSH Cut for help.' + e.message)
                    result = { error: e.message }
                }
            }
        }

        clearTimeout(timer.current)
        timer.current = null
        hideLoadingOverlay()

        if (options.resultCallback) {
            options.resultCallback(result)
        }
        return result
    }

    function handleTimeout(message: ReactNode, callback?: () => void) {
        cancel()
        if (message != null) {
            showAlert('Timeout', message)
        }
        if (callback != null) {
            callback()
        }
    }

    function cancel() {
        if (timer.current === null) {
            return
        }

        hideLoadingOverlay()
        abortController.current?.abort()
        clearTimeout(timer.current)
        timer.current = null
    }

    const combinedContent = <>{overlayContent}{alertContent}</>

    return [combinedContent, execute, cancel] as const
}
